import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQbotAccessToken } from '@/lib/logto';
import type {
  AdvisorDecksResponse,
  AdvisorUpgradesResponse,
  BattlesResponse,
  CoachBuildResponse,
  CoachDeckResponse,
  CoachMatchupResponse,
  CompleteDeckResponse,
  CounterDeckResponse,
  CrIdentity,
  DeckCheckResponse,
  DeckRateResponse,
  DoctorSeedCard,
  LinkPlayerResponse,
  MetaCardsResponse,
  PlayerByTagResponse,
  PopularDecksResponse,
  ProfileResponse,
  PublicCard,
  SavedDeck,
} from '@/types/qbot';

/**
 * QBot API client. Talks to qbot-api at /api/v1/cr/*.
 *
 * Auth: Logto Bearer JWT with audience `https://qbot.qlash.gg`, minted
 * by getQbotAccessToken() via refresh-token grant (same pattern as
 * qriotsApi). The audience is the QBot backoffice resource — already
 * registered on the staging+prod Logto tenants — so no new Logto setup
 * is required for the Mini App.
 *
 * Dev: env.qbotApiUrl = '/qbot-api', Vite proxy forwards to MacMini.
 * Prod: env.qbotApiUrl = 'https://qbot.qlash.gg'.
 *
 * Tag invalidation:
 *   - 'Identity' refetches `/me` after link/unlink
 *   - 'Profile' refetches `/profile/me` after link
 *   - 'SavedDecks' refetches `/decks/saved` after create/delete
 */
export const qbotApi = createApi({
  reducerPath: 'qbotApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qbotApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQbotAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('Accept', 'application/json');
        return headers;
      },
    }),
    { maxRetries: 1 },
  ),
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: ['Identity', 'Profile', 'SavedDecks'],
  endpoints: (builder) => ({
    // ─── Identity + card catalog ────────────────────────────────────────

    me: builder.query<CrIdentity, void>({
      query: () => '/api/v1/cr/me',
      transformResponse: (raw: { data: CrIdentity }) => raw.data,
      providesTags: ['Identity'],
    }),

    cards: builder.query<PublicCard[], void>({
      query: () => '/api/v1/cr/cards',
      transformResponse: (raw: { data: { cards: PublicCard[]; count: number } }) => raw.data.cards,
      keepUnusedDataFor: 86400, // 24h — catalog is near-static
    }),

    // ─── Profile ────────────────────────────────────────────────────────

    profileMe: builder.query<ProfileResponse, void>({
      query: () => '/api/v1/cr/profile/me',
      transformResponse: (raw: { data: ProfileResponse }) => raw.data,
      providesTags: ['Profile'],
    }),

    profileByTag: builder.query<ProfileResponse, { tag: string }>({
      query: ({ tag }) => `/api/v1/cr/profile/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: ProfileResponse }) => raw.data,
    }),

    /**
     * Identity bridge (CR side, PRIMARY): resolve a Clash Royale tag → the
     * owner's Logto `sub` (GET /api/v1/cr/players/by-tag/:tag). Broader source
     * than qduels' link table — QBot maps any player it has seen (SavedPlayer
     * cache, read-through-seeded from the QLASH platform), so this resolves
     * more opponents. The qduels query is the fallback when this misses.
     *
     * 404 `{ error: { code: "NOT_FOUND" } }` (tag not mapped) and 400
     * `{ error: { code: "INVALID_TAG" } }` are NOT hard failures — they stay
     * on the error channel so callers read them as "not resolved" and try the
     * qduels fallback (then render no social buttons if that misses too). The
     * retry baseQuery already skips retrying 4xx.
     */
    playerByTag: builder.query<PlayerByTagResponse, string>({
      query: (tag) => `/api/v1/cr/players/by-tag/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: PlayerByTagResponse }) => raw.data,
      keepUnusedDataFor: 600,
    }),

    linkPlayer: builder.mutation<LinkPlayerResponse, { tag: string }>({
      query: (body) => ({ url: '/api/v1/cr/profile/link', method: 'POST', body }),
      transformResponse: (raw: { data: LinkPlayerResponse }) => raw.data,
      invalidatesTags: ['Identity', 'Profile'],
    }),

    unlinkPlayer: builder.mutation<{ unlinked: boolean; removedRows: number }, void>({
      query: () => ({ url: '/api/v1/cr/profile/link', method: 'DELETE' }),
      transformResponse: (raw: { data: { unlinked: boolean; removedRows: number } }) => raw.data,
      invalidatesTags: ['Identity', 'Profile'],
    }),

    // ─── Decks ──────────────────────────────────────────────────────────

    popularDecks: builder.query<
      PopularDecksResponse,
      { archetype?: string; cards?: string[]; page?: number; pageSize?: number }
    >({
      query: ({ archetype, cards, page = 1, pageSize = 10 }) => ({
        url: '/api/v1/cr/decks/popular',
        params: {
          ...(archetype ? { archetype } : {}),
          ...(cards?.length ? { cards: cards.join(',') } : {}),
          page,
          pageSize,
        },
      }),
      transformResponse: (raw: { data: PopularDecksResponse }) => raw.data,
      keepUnusedDataFor: 1800, // 30 min — popular decks are server-side cached for 30 min too
    }),

    deckCheck: builder.query<DeckCheckResponse, { tag: string }>({
      query: ({ tag }) => `/api/v1/cr/decks/check/${encodeURIComponent(tag)}`,
      transformResponse: (raw: { data: DeckCheckResponse }) => raw.data,
    }),

    savedDecks: builder.query<{ decks: SavedDeck[]; count: number }, void>({
      query: () => '/api/v1/cr/decks/saved',
      transformResponse: (raw: { data: { decks: SavedDeck[]; count: number } }) => raw.data,
      providesTags: ['SavedDecks'],
    }),

    saveDeck: builder.mutation<{ deck: SavedDeck }, { name: string; cards: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/decks/saved', method: 'POST', body }),
      transformResponse: (raw: { data: { deck: SavedDeck } }) => raw.data,
      invalidatesTags: ['SavedDecks'],
    }),

    deleteSavedDeck: builder.mutation<{ deleted: boolean; id: number }, { id: number }>({
      query: ({ id }) => ({ url: `/api/v1/cr/decks/saved/${id}`, method: 'DELETE' }),
      transformResponse: (raw: { data: { deleted: boolean; id: number } }) => raw.data,
      invalidatesTags: ['SavedDecks'],
    }),

    // ─── Battles ────────────────────────────────────────────────────────

    battles: builder.query<
      BattlesResponse,
      { tag: string; page?: number; pageSize?: number; analyze?: boolean; fresh?: boolean }
    >({
      // `fresh=true` (sent by the Refresh button) asks qbot to bypass its cache
      // + the qCrC collector and read the battle log directly from Supercell —
      // the only way to surface a just-finished match before the collector's
      // ~6-min cycle. It's also a distinct RTK cache key, so switching to fresh
      // forces a fetch instead of serving the cached collector list.
      query: ({ tag, page = 1, pageSize = 10, analyze = false, fresh = false }) => ({
        url: `/api/v1/cr/battles/${encodeURIComponent(tag)}`,
        params: {
          page,
          pageSize,
          // `analyze` + `BattlesResponse.analysis` document a still-existing
          // server contract; the TMA no longer sends/reads it (analyze toggle
          // was removed from the UI). Retained for parity, not currently used.
          ...(analyze ? { analyze: 'true' } : {}),
          ...(fresh ? { fresh: 'true' } : {}),
        },
      }),
      transformResponse: (raw: { data: BattlesResponse }) => raw.data,
    }),

    // ─── AI coaching (pro tier required) ────────────────────────────────

    coachDeck: builder.mutation<CoachDeckResponse, { tag: string }>({
      query: ({ tag }) => ({
        url: `/api/v1/cr/coach/deck/${encodeURIComponent(tag)}`,
        method: 'POST',
      }),
      transformResponse: (raw: { data: CoachDeckResponse }) => raw.data,
    }),

    /**
     * Analyse an ARBITRARY deck (list of card names) rather than the player's
     * current deck. Powers the per-deck "AI analysis" button on the saved /
     * popular deck lists where there's no tag to anchor the analysis to.
     *
     * Returns the same `CoachDeckResponse` shape as `coachDeck` (server reuses
     * the same coach prompt, just seeded from the card list). Pro-gated +
     * rate-limited server-side, same as the other coach endpoints.
     *
     * NOTE: the QBot endpoint `POST /api/v1/cr/coach/deck-list` is being built
     * in parallel — wired here ahead of the deploy.
     */
    coachDeckList: builder.mutation<CoachDeckResponse, { cards: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/coach/deck-list', method: 'POST', body }),
      transformResponse: (raw: { data: CoachDeckResponse }) => raw.data,
    }),

    coachBuild: builder.mutation<CoachBuildResponse, { style?: string; keptCards?: string[] }>({
      query: (body) => ({ url: '/api/v1/cr/coach/build', method: 'POST', body }),
      transformResponse: (raw: { data: CoachBuildResponse }) => raw.data,
    }),

    coachMatchup: builder.mutation<
      CoachMatchupResponse,
      { myTag: string; opponentTag: string; gameMode?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/coach/matchup', method: 'POST', body }),
      transformResponse: (raw: { data: CoachMatchupResponse }) => raw.data,
    }),

    // ─── Deck rating (arbitrary 8-card deck — deck builder) ─────────────
    rateDeck: builder.mutation<DeckRateResponse, { cards: Array<string | number> }>({
      query: (body) => ({ url: '/api/v1/cr/decks/rate', method: 'POST', body }),
      transformResponse: (raw: { data: DeckRateResponse }) => raw.data,
    }),

    // ─── Advisor / deck-doctor (qCrC, deterministic, free-tier) ─────────
    // Operate on the caller's linked CR tag (resolved server-side); 404
    // NOT_LINKED when no tag. Can trigger a live upstream fetch → slow (~15s).
    advisorUpgrades: builder.query<
      AdvisorUpgradesResponse,
      { segment?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: '/api/v1/cr/advisor/upgrades',
        params: {
          ...(args?.segment ? { segment: args.segment } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      transformResponse: (raw: { data: AdvisorUpgradesResponse }) => raw.data,
    }),

    advisorDecks: builder.query<AdvisorDecksResponse, { segment?: string; limit?: number } | void>({
      query: (args) => ({
        url: '/api/v1/cr/advisor/decks',
        params: {
          ...(args?.segment ? { segment: args.segment } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      transformResponse: (raw: { data: AdvisorDecksResponse }) => raw.data,
    }),

    completeDeck: builder.mutation<
      CompleteDeckResponse,
      { seedCards?: DoctorSeedCard[]; mode?: string; archetype?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/advisor/complete-deck', method: 'POST', body }),
      transformResponse: (raw: { data: CompleteDeckResponse }) => raw.data,
    }),

    counterDeck: builder.mutation<
      CounterDeckResponse,
      { opponentDeck: Array<string | number>; mode?: string }
    >({
      query: (body) => ({ url: '/api/v1/cr/advisor/counter-deck', method: 'POST', body }),
      transformResponse: (raw: { data: CounterDeckResponse }) => raw.data,
    }),

    // ─── Meta trend (best cards + risers/fallers) ───────────────────────
    // Best-effort upstream: returns { cards: [] } on error, never 429.
    metaCards: builder.query<
      MetaCardsResponse,
      { segment?: string; window?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: '/api/v1/cr/meta/cards',
        params: {
          ...(args?.segment ? { segment: args.segment } : {}),
          ...(args?.window ? { window: args.window } : {}),
          ...(args?.limit ? { limit: args.limit } : {}),
        },
      }),
      transformResponse: (raw: { data: MetaCardsResponse }) => raw.data,
      keepUnusedDataFor: 1800, // 30 min — the meta refreshes every ~2h/6h
    }),
  }),
});

export const {
  useMeQuery,
  useCardsQuery,
  useProfileMeQuery,
  useProfileByTagQuery,
  usePlayerByTagQuery,
  useLinkPlayerMutation,
  useUnlinkPlayerMutation,
  usePopularDecksQuery,
  useDeckCheckQuery,
  useSavedDecksQuery,
  useSaveDeckMutation,
  useDeleteSavedDeckMutation,
  useBattlesQuery,
  useCoachDeckMutation,
  useCoachDeckListMutation,
  useCoachBuildMutation,
  useCoachMatchupMutation,
  useRateDeckMutation,
  useAdvisorUpgradesQuery,
  useAdvisorDecksQuery,
  useCompleteDeckMutation,
  useCounterDeckMutation,
  useMetaCardsQuery,
} = qbotApi;

/**
 * Build a CDN-ish URL for a card icon served by qbot-api.
 * Used by <CardIcon> to render PNG art without bundling the assets.
 */
export function qbotCardIconUrl(id: number, form: 'base' | 'evolution' | 'hero' = 'base'): string {
  const q = form === 'base' ? '' : `?form=${form}`;
  return `${env.qbotApiUrl}/api/v1/cr/cards/${id}/icon${q}`;
}
